import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import '@/styles/milk-landing.css';

const SERIF = "'Instrument Serif', Georgia, serif";
const PHOTO_POUR = '/assets/photography/pour.jpg';

const VALUES = [
  { t: 'Capture, never chase', d: 'The work should follow the conversation automatically — not live in a notebook you forget to reopen.' },
  { t: 'Clarity over volume', d: 'One clear Action Card beats ten pages of notes. We surface the decision, the owner, and the next step.' },
  { t: 'Your words, your data', d: 'Your conversations are yours. We process only what we must, and we never train third-party models on your content.' },
  { t: 'Momentum by default', d: 'Every capture ends with the work already moving — follow-up drafted, tasks assigned, reminders set.' },
];

const STATS = [
  { n: '<60s', l: 'From talk to Action Card' },
  { n: '3', l: 'Ways to capture — call, meeting, site visit' },
  { n: '0', l: 'Notes you have to write yourself' },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goSignup = () => navigate(user ? '/' : '/register');

  return (
    <div className="mlk-land" data-testid="about-page">
      <MarketingHeader linkPrefix="/#" />

      <main>
        {/* HERO */}
        <section style={{ padding: '72px 0 40px' }}>
          <div className="container" style={{ maxWidth: 860 }}>
            <p className="eyebrow">About Milk</p>
            <h1 style={{ fontFamily: SERIF, fontSize: 56, lineHeight: 1.04, color: '#212E40', margin: '14px 0 18px' }}>
              We built Milk so the work<br /><em style={{ color: '#4C73A6' }}>keeps moving after you talk.</em>
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.65, color: '#5A6675', maxWidth: 680 }}>
              Great conversations happen every day — on calls, in meetings, out in the field. And then the
              momentum evaporates into half-finished notes. Milk is the execution layer for those conversations:
              it captures what was said and turns it into follow-ups, tasks, and next steps before you've even hung up.
            </p>
          </div>
        </section>

        {/* PHOTO band */}
        <section style={{ padding: '20px 0 8px' }}>
          <div className="container">
            <div style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 22px 48px rgba(33,46,64,.10)', border: '1px solid rgba(33,46,64,.08)' }}>
              <img src={PHOTO_POUR} alt="Milk" style={{ width: '100%', height: 340, objectFit: 'cover' }} />
            </div>
          </div>
        </section>

        {/* STORY */}
        <section style={{ padding: '56px 0 24px' }}>
          <div className="container" style={{ maxWidth: 780 }}>
            <p className="eyebrow">Why we exist</p>
            <div style={{ fontSize: 18, lineHeight: 1.75, color: '#3C4654', marginTop: 14, display: 'grid', gap: 18 }}>
              <p>
                We kept watching capable people lose good work to the gap between talking and doing. The decision
                was made, the promise given, the follow-up agreed — and then it slipped, because someone had to
                remember to write it down and act on it later.
              </p>
              <p>
                Milk closes that gap. You focus on the conversation; Milk listens, understands, and hands you a
                ready-to-act Action Card — summary, decisions, tasks, and a drafted follow-up — in under a minute.
                No note-taking, no re-typing, nothing forgotten.
              </p>
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section style={{ padding: '40px 0 24px' }}>
          <div className="container">
            <p className="eyebrow" style={{ textAlign: 'center' }}>What we believe</p>
            <h2 style={{ fontFamily: SERIF, fontSize: 40, color: '#212E40', textAlign: 'center', margin: '10px 0 40px' }}>
              Principles that shape the product
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              {VALUES.map((v, i) => (
                <div key={i} data-testid={`about-value-${i}`} style={{ background: '#fff', border: '1px solid rgba(33,46,64,.08)', borderRadius: 18, padding: '26px 24px', boxShadow: '0 1px 2px rgba(33,46,64,.04)' }}>
                  <h3 style={{ fontFamily: SERIF, fontSize: 24, color: '#212E40', marginBottom: 10 }}>{v.t}</h3>
                  <p style={{ fontSize: 15.5, lineHeight: 1.7, color: '#5A6675' }}>{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STATS */}
        <section style={{ padding: '40px 0 24px' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, background: '#EEEAE0', borderRadius: 20, padding: '40px 28px' }}>
              {STATS.map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: SERIF, fontSize: 48, color: '#4C73A6', lineHeight: 1 }}>{s.n}</div>
                  <div style={{ fontSize: 15, color: '#5A6675', marginTop: 8 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta" style={{ padding: '56px 0 72px' }}>
          <div className="container">
            <div className="cta-band">
              <h2>Ready to stop taking notes?</h2>
              <p>Start free and let Milk keep you on top of every conversation.</p>
              <div className="cta-actions">
                <button className="btn btn-cream btn-lg" onClick={goSignup} data-testid="about-cta-button">Get started free</button>
              </div>
              <div className="cta-mini">No credit card · Set up in two minutes</div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter linkPrefix="/#" />
    </div>
  );
}
